import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './UserDetail.css';
import { usersAPI, picturesAPI } from './services/api';

function UserDetail({ userId, onClose }) {
  const { t, i18n } = useTranslation();
  const [userDetails, setUserDetails] = useState(null);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pictures, setPictures] = useState([]);
  const [selectedPictureIndex, setSelectedPictureIndex] = useState(null);

  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const [detailsData, hashtagsData, picturesData] = await Promise.all([
          usersAPI.getUserDetails(userId),
          usersAPI.getUserHashtags(userId),
          picturesAPI.getUserPictures(userId)
        ]);
        setUserDetails(detailsData);
        setHashtags(hashtagsData);
        setPictures(picturesData);
      } catch (err) {
        console.error('Error loading user details:', err);
        setError(t('userDetail.error'));
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadUserDetails();
    }
  }, [userId]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedPictureIndex === null) return;
      if (e.key === 'Escape') setSelectedPictureIndex(null);
      else if (e.key === 'ArrowLeft' && selectedPictureIndex > 0) setSelectedPictureIndex(selectedPictureIndex - 1);
      else if (e.key === 'ArrowRight' && selectedPictureIndex < pictures.length - 1) setSelectedPictureIndex(selectedPictureIndex + 1);
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedPictureIndex, pictures.length]);

  // Helper function to get avatar color based on count
  const getAvatarColor = (count, baseColorRgb) => {
    if (count === 0) return { bg: '#e0e0e0', text: '#666' };

    const [r, g, b] = baseColorRgb;
    const darkenFactor = Math.min(count / 10, 1);
    const lightness = 1 - (darkenFactor * 0.5);

    const adjustedR = Math.round(r * lightness);
    const adjustedG = Math.round(g * lightness);
    const adjustedB = Math.round(b * lightness);

    return {
      bg: `rgb(${adjustedR}, ${adjustedG}, ${adjustedB})`,
      text: darkenFactor > 0.4 ? '#ffffff' : '#333333'
    };
  };

  // Get base color for avatar type
  const getAvatarBaseColor = (avatarType) => {
    switch (avatarType) {
      case 'activity':
        return [45, 125, 74]; // Green
      case 'public-art':
        return [33, 150, 243]; // Blue
      case 'public-music':
        return [156, 39, 176]; // Purple
      case 'public-sport':
        return [255, 152, 0]; // Orange
      default:
        return [45, 125, 74];
    }
  };

  // Get avatar type display name
  const getAvatarTypeName = (avatarType) => {
    switch (avatarType) {
      case 'activity':
        return t('userDetail.avatarTypes.activity');
      case 'public-art':
        return t('userDetail.avatarTypes.publicArt');
      case 'public-music':
        return t('userDetail.avatarTypes.publicMusic');
      case 'public-sport':
        return t('userDetail.avatarTypes.publicSport');
      default:
        return avatarType;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="user-detail-overlay" onClick={onClose}>
        <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="user-detail-loading">{t('userDetail.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="user-detail-overlay" onClick={onClose}>
        <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="user-detail-error">{error || t('userDetail.notFound')}</div>
          <button className="close-btn" onClick={onClose}>{t('userDetail.close')}</button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="user-detail-overlay" onClick={onClose}>
      <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✕</button>

        <div className="user-detail-header">
          <h2>{userDetails.displayName}</h2>
          {(userDetails.age || userDetails.bio) && (
            <div className="user-detail-meta">
              {userDetails.age && <span className="user-detail-age">{t('userDetail.age', { age: userDetails.age })}</span>}
              {userDetails.bio && <p className="user-detail-bio">{userDetails.bio}</p>}
            </div>
          )}
        </div>

        {/* Avatars Section */}
        {userDetails.avatars.length > 0 && (
          <div className="avatars-section">
            <h3>{t('userDetail.avatarProgress')}</h3>
            <div className="avatars-grid">
              {userDetails.avatars.map((avatar, index) => {
                const baseColor = getAvatarBaseColor(avatar.avatarType);
                const colors = getAvatarColor(avatar.lastMonthCount, baseColor);

                return (
                  <div key={index} className="avatar-card">
                    <div className="avatar-type-name">{getAvatarTypeName(avatar.avatarType)}</div>
                    <div
                      className="avatar-circle-large"
                      style={{ backgroundColor: colors.bg, color: colors.text }}
                    >
                      {avatar.totalCount}
                    </div>
                    <div className="avatar-stats">
                      <div className="stat">
                        <span className="stat-label">{t('userDetail.stats.total')}</span>
                        <span className="stat-value">{avatar.totalCount}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">{t('userDetail.stats.lastMonth')}</span>
                        <span className="stat-value">{avatar.lastMonthCount}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Event Interests Section */}
        {hashtags.length > 0 && (
          <div className="interests-section">
            <h3>{t('userDetail.eventInterests')}</h3>
            <div className="hashtags-list">
              {hashtags.map((hashtag, index) => (
                <div key={index} className="hashtag-item">
                  <span className="hashtag-name">#{hashtag.hashtag}</span>
                  <span className="hashtag-count">{hashtag.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Section */}
        {pictures.length > 0 && (
          <div className="ud-gallery-section">
            <h3>{t('userDetail.gallery', { count: pictures.length })}</h3>
            <div className="ud-gallery-grid">
              {pictures.map((picture, index) => (
                <div
                  key={picture.id}
                  className="ud-gallery-item"
                  onClick={() => setSelectedPictureIndex(index)}
                >
                  <img
                    src={picturesAPI.getImage(picture.picSysName, picture.connectedToUsername)}
                    alt={picture.picUserName}
                    className="ud-gallery-img"
                  />
                  <div className="ud-gallery-caption">{picture.eventName || picture.communityEventName || ''}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Badges Section */}
        {userDetails.badges.length > 0 ? (
          <div className="badges-section">
            <h3>{t('userDetail.starsReceived', { count: userDetails.badges.length })}</h3>
            <div className="badges-list">
              {userDetails.badges.map((badge) => (
                <div key={badge.id} className="badge-card">
                  <div className="badge-header">
                    <div className="badge-icon">⭐</div>
                    <div className="badge-info">
                      {!badge.isAnonymous && (
                        <div className="badge-name">
                          {badge.givenByDisplayName}
                        </div>
                      )}
                      <div className="badge-date">{formatDate(badge.createdAt)}</div>
                    </div>
                  </div>
                  {badge.message && !badge.isPrivate ? (
                    <div className="badge-message">
                      <em>"{badge.message}"</em>
                    </div>
                  ) : null}
                  {badge.eventName && !badge.isAnonymous && (
                    <div className="badge-details">
                      <div className="badge-event">{t('userDetail.event', { name: badge.eventName })}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="badges-section">
            <h3>{t('userDetail.starsReceivedEmpty')}</h3>
            <div className="no-badges">{t('userDetail.noStars')}</div>
          </div>
        )}
      </div>
    </div>

    {selectedPictureIndex !== null && pictures[selectedPictureIndex] && (
      <div className="ud-fullscreen-overlay" onClick={() => setSelectedPictureIndex(null)}>
        <button className="ud-fullscreen-close" onClick={() => setSelectedPictureIndex(null)}>×</button>

        {selectedPictureIndex > 0 && (
          <button className="ud-fullscreen-nav ud-fullscreen-prev" onClick={(e) => { e.stopPropagation(); setSelectedPictureIndex(selectedPictureIndex - 1); }}>
            ‹
          </button>
        )}

        <div className="ud-fullscreen-content" onClick={(e) => e.stopPropagation()}>
          <img
            src={picturesAPI.getImage(pictures[selectedPictureIndex].picSysName, pictures[selectedPictureIndex].connectedToUsername)}
            alt={pictures[selectedPictureIndex].picUserName}
            className="ud-fullscreen-image"
          />
          <div className="ud-fullscreen-caption">
            {(pictures[selectedPictureIndex].eventName || pictures[selectedPictureIndex].communityEventName) && (
              <p>{pictures[selectedPictureIndex].eventName || pictures[selectedPictureIndex].communityEventName}</p>
            )}
            <p className="ud-fullscreen-counter">{selectedPictureIndex + 1} / {pictures.length}</p>
          </div>
        </div>

        {selectedPictureIndex < pictures.length - 1 && (
          <button className="ud-fullscreen-nav ud-fullscreen-next" onClick={(e) => { e.stopPropagation(); setSelectedPictureIndex(selectedPictureIndex + 1); }}>
            ›
          </button>
        )}
      </div>
    )}
    </>
  );
}

export default UserDetail;
